import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Paper,
    TextField,
    IconButton
} from '@mui/material';
import { QrCodeIcon, KeyboardIcon, CloseIcon } from 'icons/materialIcons';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getUserAccessories, deleteAccessory, unmapAccessory, activateAccessory, getAccessoryBySerialNumber } from 'apis/accessoryApi';
import { getCardsByUser } from 'apis/userApi';
import { useAuth } from 'contexts/AuthContext';
import TeamAccessoryTable from './TeamAccessoryTable';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import ScannerErrorBoundary from 'utils/ScannerErrorBoundary';
import { isMobileDevice } from 'utils/deviceDetector';

const TeamAccessoryDashboard = ({ onEditAccessory }) => {
    const [accessories, setAccessories] = useState([]);
    const [userCards, setUserCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Scanner dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [serialNumber, setSerialNumber] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [scannerMessage, setScannerMessage] = useState('');
    const [scannerLoading, setScannerLoading] = useState(false);
    const [processingActivation, setProcessingActivation] = useState(false);
    const [scannerState, setScannerState] = useState('idle'); // 'idle', 'initializing', 'ready', 'cleaning'

    const scannerContainerRef = useRef(null);
    const scannerRef = useRef(null);
    const scannerId = 'qr-reader-container';

    const { cardId } = useParams();
    const { authState } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isCardLevel = location.pathname.includes('/mycards/edit/');
    const isMobile = isMobileDevice();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const cardsResponse = await getCardsByUser(authState.user?.userId);
                setUserCards(cardsResponse);

                if (isCardLevel && cardId) {
                    setSelectedCardId(cardId);
                }
            } catch (error) {
                console.error('Error fetching cards:', error);
                setNotification({
                    open: true,
                    message: 'Failed to fetch cards',
                    severity: 'error'
                });
            }
        };

        if (authState.user?.userId) {
            fetchData();
        }
    }, [authState.user?.userId, cardId, isCardLevel]);

    useEffect(() => {
        fetchAccessories();
    }, [authState.user?.userId, selectedCardId]);

    const fetchAccessories = async () => {
        if (!authState.user?.userId) return;

        try {
            setLoading(true);
            const response = await getUserAccessories(authState.user.userId);
            let filteredAccessories = response.accessories;

            if (selectedCardId && selectedCardId !== 'all') {
                filteredAccessories = response.accessories.filter(accessory =>
                    accessory.content?.data?.cardId === selectedCardId
                );
            }

            setAccessories(filteredAccessories);
        } catch (error) {
            console.error('Error fetching accessories:', error);
            setError(error.message || 'Failed to fetch accessories');
            setNotification({
                open: true,
                message: error.message || 'Failed to fetch accessories',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccessory = async (accessory) => {
        try {
            await deleteAccessory(accessory.serialNumber);
            await fetchAccessories(); // Refresh the list after successful deletion
            setNotification({
                open: true,
                message: 'Accessory deleted successfully',
                severity: 'success'
            });
            return true;
        } catch (error) {
            console.error('Error deleting accessory:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to delete accessory',
                severity: 'error'
            });
            throw new Error(error.message || 'Failed to delete accessory');
        }
    };

    const handleUnmapAccessory = async (accessory) => {
        try {
            await unmapAccessory(accessory.serialNumber);
            await fetchAccessories(); // Refresh the list after successful unmapping
            setNotification({
                open: true,
                message: 'Accessory unmapped successfully',
                severity: 'success'
            });
            return true;
        } catch (error) {
            console.error('Error unmapping accessory:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to unmap accessory',
                severity: 'error'
            });
            throw new Error(error.message || 'Failed to unmap accessory');
        }
    };

    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Clean up scanner safely
    const destroyScanner = () => {
        if (window.currentScanner) {
            try {
                // Try to stop first (which is safer)
                if (typeof window.currentScanner.stop === 'function') {
                    window.currentScanner.stop();
                }

                // Small delay before clearing
                setTimeout(() => {
                    try {
                        window.currentScanner.clear();
                        window.currentScanner = null;
                        console.log("Scanner destroyed");
                    } catch (err) {
                        console.warn("Error clearing scanner:", err);
                        // Force cleanup on error
                        window.currentScanner = null;
                    }
                    // Always set state back to idle after cleanup
                    setScannerState('idle');
                }, 200); // Increased timeout for more reliable cleanup
            } catch (err) {
                console.warn("Error destroying scanner:", err);
                // Force cleanup on error
                window.currentScanner = null;
                setScannerState('idle');
            }
        } else {
            // No scanner to destroy, just reset state
            setScannerState('idle');
        }
    };

    // Open scanner dialog
    const handleScanAccessory = () => {
        setSerialNumber('');
        setScannerMessage('');
        setTabValue(isMobile ? 0 : tabValue); // Force QR scanner tab on mobile
        setScannerLoading(true);
        setDialogOpen(true);
    };

    // Handle dialog close - make sure to clean up
    const handleDialogClose = () => {
        destroyScanner();
        // Small delay to ensure cleanup is complete
        setTimeout(() => {
            setDialogOpen(false);
            setProcessingActivation(false);
        }, 250);
    };

    // Direct activation for mobile (bypasses confirmation screen)
    const handleDirectActivation = async (scannedSerialNumber, isLegacy = false) => {
        if (processingActivation) return;

        setProcessingActivation(true);
        setScannerMessage('Checking accessory...');

        try {
            // First verify the accessory exists
            const queryParams = isLegacy ? '?isLegacy=true' : '';
            const response = await getAccessoryBySerialNumber(scannedSerialNumber, queryParams);

            if (!response || !response.accessory) {
                setScannerMessage('Invalid accessory. Please check the serial number.');
                setTimeout(() => {
                    handleDialogClose();
                }, 2000);
                return;
            }

            // Check if accessory is already activated
            if (response.accessory.status === 'activated') {
                setScannerMessage('This accessory is already activated.');
                setTimeout(() => {
                    handleDialogClose();
                    if (response.accessory.content?.data?.cardId) {
                        navigate(`/card/${response.accessory.content.data.cardId}`);
                    }
                }, 1500);
                return;
            }

            // Verify accessory is available for activation
            if (response.accessory.status !== 'available' && response.accessory.status !== 'purchased') {
                setScannerMessage(`This accessory cannot be activated (status: ${response.accessory.status}).`);
                setTimeout(() => {
                    handleDialogClose();
                }, 2000);
                return;
            }

            setScannerMessage('Activating accessory...');

            // Get default card if available
            let defaultCardId = null;
            if (userCards && userCards.length > 0) {
                // Use selectedCardId if not 'all', otherwise use first card
                defaultCardId = selectedCardId !== 'all' ? selectedCardId : userCards[0]._id;
            }

            if (!defaultCardId) {
                setScannerMessage('No card available for activation');
                setTimeout(() => {
                    handleDialogClose();
                    navigate('/team/members');
                }, 2000);
                return;
            }

            // Activate the accessory directly
            const activationData = {
                userId: authState.user.userId,
                contentType: 'business_card',
                contentData: {
                    cardId: defaultCardId,
                    type: 'card_link'
                }
            };

            const result = await activateAccessory(scannedSerialNumber, activationData);

            if (result && result.success) {
                setScannerMessage('Accessory activated successfully!');
                setTimeout(() => {
                    handleDialogClose();
                    // Redirect to card
                    if (result.accessory.content?.data?.cardId) {
                        navigate(`/card/${result.accessory.content.data.cardId}`);
                    } else {
                        fetchAccessories(); // Refresh the accessories list
                    }
                }, 1500);
            } else {
                throw new Error('Activation failed');
            }
        } catch (error) {
            console.error('Activation error:', error);
            setScannerMessage(`Activation failed: ${error.message || 'Unknown error'}`);
            setTimeout(() => {
                handleDialogClose();
            }, 2000);
        }
    };

    // Handle successful QR scan
    const handleQrCodeSuccess = (decodedText) => {
        // Extract serial number from QR code URL if needed
        let scannedSerialNumber = decodedText;
        let isLegacy = false;

        // Check for legacy URL format (app.intouchwe.com/view-profile/XXXX)
        const legacyPatterns = [
            /intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i,
            /app\.intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i,
            /www\.intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i
        ];

        // Try each legacy pattern
        for (const pattern of legacyPatterns) {
            const match = decodedText.match(pattern);
            if (match && match[1]) {
                console.log(`Legacy URL detected: ${decodedText}`);
                scannedSerialNumber = match[1];
                isLegacy = true;
                console.log(`Extracted legacy ID: ${scannedSerialNumber}`);
                break;
            }
        }

        // If not a legacy URL, check for new format
        if (!isLegacy) {
            const urlMatch = decodedText.match(/\/accessory\/scan\/([A-Za-z0-9-]+)/);
            if (urlMatch && urlMatch[1]) {
                scannedSerialNumber = urlMatch[1];
            }
        }

        console.log(`QR Code detected: ${decodedText}`);
        setScannerMessage(`Success! ${isLegacy ? 'Legacy ID' : 'Serial number'}: ${scannedSerialNumber}`);

        // On mobile, activate directly. On desktop, show confirmation screen
        if (isMobile) {
            handleDirectActivation(scannedSerialNumber, isLegacy);
        } else {
            // Stop scanning and navigate after a short delay
            setTimeout(() => {
                destroyScanner();
                handleDialogClose();
                const queryParams = isLegacy ? '?isLegacy=true' : '';
                navigate(`/accessory/scan/${scannedSerialNumber}${queryParams}`);
            }, 1000);
        }
    };

    // Handle manual serial number entry
    const handleMockScan = () => {
        if (serialNumber) {
            // Check if this might be a legacy ID (simple pattern matching)
            const isLegacy = /^[A-Z0-9]{5,7}$/.test(serialNumber);
            const queryParams = isLegacy ? '?isLegacy=true' : '';

            if (isMobile) {
                handleDirectActivation(serialNumber, isLegacy);
            } else {
                handleDialogClose();
                navigate(`/accessory/scan/${serialNumber}${queryParams}`);
            }
        }
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        // Don't allow tab change during critical scanner operations
        if (scannerState === 'initializing' || scannerState === 'cleaning') {
            // Show temporary message that tab switching isn't available yet
            setScannerMessage('Please wait until scanner is ready before switching tabs...');
            return; // Don't change the tab
        }

        if (newValue !== 0) {
            // Set state to cleaning before destroying
            setScannerState('cleaning');
            // Destroy scanner when switching away from scanner tab
            destroyScanner();
        }

        setTabValue(newValue);
    };

    // Initialize or destroy the scanner when the tab changes or dialog state changes
    useEffect(() => {
        if (!dialogOpen || tabValue !== 0) {
            destroyScanner();
            return;
        }

        setScannerLoading(true);
        setScannerState('initializing'); // Mark as initializing

        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
            // First, ensure any existing scanner is destroyed
            destroyScanner();

            // Get the container element
            const container = document.getElementById(scannerId);
            if (!container) {
                console.error("Scanner container not found!");
                setScannerLoading(false);
                setScannerMessage("Scanner container not found. Please try again.");
                setScannerState('idle'); // Reset state on error
                return;
            }

            // Clear the container
            container.innerHTML = '';

            try {
                // Dynamically import HTML5QrCode to avoid React rendering issues
                import('html5-qrcode').then(({ Html5QrcodeScanner }) => {
                    try {
                        const config = {
                            fps: 10,
                            qrbox: { width: 250, height: 250 },
                            rememberLastUsedCamera: true,
                        };

                        // Create the scanner outside of React's rendering cycle
                        window.currentScanner = new Html5QrcodeScanner(
                            scannerId,
                            config,
                            false
                        );

                        // Render the scanner
                        window.currentScanner.render(
                            handleQrCodeSuccess,
                            (errorMessage) => {
                                console.log(`QR Error: ${errorMessage}`);
                            }
                        );

                        setScannerLoading(false);
                        setScannerState('ready'); // Scanner is ready
                    } catch (error) {
                        console.warn("Error initializing scanner:", error);
                        setScannerMessage("Could not initialize camera. Please use manual entry or check camera permissions.");
                        setScannerLoading(false);
                        setScannerState('idle'); // Reset state on error
                    }
                }).catch(err => {
                    console.warn("Error importing html5-qrcode:", err);
                    setScannerLoading(false);
                    setScannerMessage("Failed to load scanner library. Please try again or use manual entry.");
                    setScannerState('idle'); // Reset state on error
                });
            } catch (error) {
                console.warn("Error in scanner setup:", error);
                setScannerMessage("Could not set up camera. Please use manual entry.");
                setScannerLoading(false);
                setScannerState('idle'); // Reset state on error
            }
        }, 300);

        return () => {
            clearTimeout(timer);
            destroyScanner();
        };
    }, [dialogOpen, tabValue, navigate, isMobile]);

    // Clean up on component unmount
    useEffect(() => {
        return () => {
            destroyScanner();
        };
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && accessories.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        color: '#4f4f4f',
                    }}
                >
                    {isCardLevel ? 'Card Accessories' : 'My Accessories'}
                </Typography>

                {/* Scan Accessory button */}
                {!isCardLevel && (
                    <Button
                        variant="contained"
                        startIcon={<QrCodeIcon />}
                        onClick={handleScanAccessory}
                        sx={{
                            backgroundColor: '#000',
                            color: '#fff',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: '500',
                            padding: { xs: '10px 16px', sm: '8px 16px' },
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            width: { xs: '100%', sm: 'auto' },
                            '&:hover': {
                                backgroundColor: '#333',
                            },
                        }}
                    >
                        Scan Accessory
                    </Button>
                )}
            </Box>

            <TeamAccessoryTable
                accessories={accessories}
                userCards={userCards}
                onEditAccessory={onEditAccessory}
                onDeleteAccessory={handleDeleteAccessory}
                onUnmapAccessory={handleUnmapAccessory}
                selectedCardId={selectedCardId}
                setSelectedCardId={setSelectedCardId}
                isCardLevel={isCardLevel}
            />

            {/* Scanner Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                fullWidth
                maxWidth={isMobile ? "xs" : "sm"}
                sx={{
                    '& .MuiDialog-paper': {
                        margin: { xs: '16px', sm: '32px' },
                        width: { xs: 'calc(100% - 32px)', sm: 'auto' }
                    }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Scan Accessory</Typography>
                        <IconButton onClick={handleDialogClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {/* Only show tabs on desktop, not on mobile */}
                    {!isMobile && (
                        <Paper sx={{ mb: 2 }}>
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                indicatorColor="primary"
                                textColor="primary"
                                centered
                            >
                                <Tab
                                    icon={<QrCodeIcon />}
                                    label="Scan QR Code"
                                    iconPosition="start"
                                />
                                <Tab
                                    icon={<KeyboardIcon />}
                                    label="Enter Serial Number"
                                    iconPosition="start"
                                />
                            </Tabs>
                        </Paper>
                    )}

                    {(tabValue === 0 || isMobile) ? (
                        <ScannerErrorBoundary>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    minHeight: '300px',
                                    width: '100%'
                                }}
                            >
                                {/* The key here is having a div that remains stable in the DOM */}
                                <div style={{ width: '100%' }}>
                                    <div id={scannerId} style={{ width: '100%' }}></div>
                                    {scannerLoading && (
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '300px',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            zIndex: 1
                                        }}>
                                            <CircularProgress size={40} sx={{ mb: 2 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                Initializing camera...
                                            </Typography>
                                        </Box>
                                    )}
                                    {processingActivation && (
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '300px',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            zIndex: 1
                                        }}>
                                            <CircularProgress size={40} sx={{ mb: 2 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                Processing...
                                            </Typography>
                                        </Box>
                                    )}
                                </div>
                                {scannerMessage && (
                                    <Typography
                                        variant="body2"
                                        color={
                                            scannerMessage.includes("Success") ||
                                                scannerMessage.includes("Activating") ||
                                                scannerMessage.includes("activated") ?
                                                "success.main" :
                                                scannerMessage.includes("Please wait until scanner") ?
                                                    "info.main" :
                                                    "error"
                                        }
                                        sx={{
                                            mt: 2,
                                            fontWeight:
                                                scannerMessage.includes("Success") ||
                                                    scannerMessage.includes("activated") ?
                                                    'bold' : 'normal'
                                        }}
                                    >
                                        {scannerMessage}
                                    </Typography>
                                )}
                            </Box>
                        </ScannerErrorBoundary>
                    ) : (
                        <Box sx={{ py: 3 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Enter the serial number found on your accessory:
                            </Typography>
                            <TextField
                                autoFocus
                                fullWidth
                                value={serialNumber}
                                onChange={(e) => setSerialNumber(e.target.value)}
                                placeholder="Enter serial number"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </DialogContent>

                {tabValue === 1 && !isMobile && (
                    <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
                        <Button
                            onClick={handleDialogClose}
                            variant="outlined"
                            sx={{
                                borderColor: '#ddd',
                                color: '#666',
                                '&:hover': {
                                    borderColor: '#bbb',
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleMockScan}
                            variant="contained"
                            disabled={!serialNumber}
                            sx={{
                                backgroundColor: '#000',
                                '&:hover': {
                                    backgroundColor: '#333'
                                }
                            }}
                        >
                            Continue
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={handleCloseNotification}
            />
        </Box>
    );
};

export default TeamAccessoryDashboard;